import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "~api";
import {
  SellerAdminChatContact,
  SellerAdminChatInboxResponse,
  SellerAdminChatMessage,
  SellerAdminChatMessagesResponse,
  SellerAdminChatTimelineItem,
} from "../types";
import { mergeChatTimeline } from "../components/utils";

const API_BASE = "/admin/admin-chat";

export const adminPeerChatKeys = {
  all: ["admin-peer-chat"] as const,
  inbox: () => [...adminPeerChatKeys.all, "inbox"] as const,
  messages: (adminId: number) =>
    [...adminPeerChatKeys.all, "messages", adminId] as const,
};

export function useAdminPeerChatInbox() {
  return useQuery<SellerAdminChatInboxResponse, Error>(
    adminPeerChatKeys.inbox(),
    async () => {
      const { data } = await api.get<SellerAdminChatInboxResponse>(
        `${API_BASE}/inbox`,
      );
      if (!data.success) {
        throw new Error("Erro ao carregar conversas da equipe");
      }
      return data;
    },
    {
      staleTime: 0,
      refetchInterval: 15000,
      refetchOnWindowFocus: true,
    },
  );
}

export function useAdminPeerChatMessages(adminId?: number) {
  return useQuery<SellerAdminChatTimelineItem[], Error>(
    adminPeerChatKeys.messages(adminId ?? 0),
    async () => {
      const { data } = await api.get<SellerAdminChatMessagesResponse>(
        `${API_BASE}/admin/${adminId}/messages`,
      );
      if (!data.success) {
        throw new Error("Erro ao carregar mensagens");
      }
      return mergeChatTimeline(data.data.messages, data.data.voice_calls);
    },
    {
      enabled: Boolean(adminId),
      staleTime: 0,
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
    },
  );
}

export function useMarkAdminPeerChatRead() {
  const queryClient = useQueryClient();

  return useMutation(async (adminId: number) => {
    await api.post(`${API_BASE}/admin/${adminId}/read`);
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries(adminPeerChatKeys.inbox());
    },
  });
}

type SendPayload = {
  adminId: number;
  body: string;
  files: File[];
};

export function useSendAdminPeerChatMessage() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ adminId, body, files }: SendPayload) => {
      const formData = new FormData();
      if (body.trim()) formData.append("body", body.trim());
      files.forEach((file) => formData.append("files[]", file));

      const { data } = await api.post(
        `${API_BASE}/admin/${adminId}/messages`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      return data.data.message as SellerAdminChatMessage;
    },
    {
      onSuccess: (_message, variables) => {
        queryClient.invalidateQueries(
          adminPeerChatKeys.messages(variables.adminId),
        );
        queryClient.invalidateQueries(adminPeerChatKeys.inbox());
      },
    },
  );
}

export function findAdminPeerContact(
  contacts: SellerAdminChatContact[],
  adminId: number,
) {
  return contacts.find(
    (contact) => contact.scope === "admin_peer" && contact.id === adminId,
  );
}
